'use client';
import { Badge, Button, Empty, List, Popover, Space, Tabs, Typography } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BellOutlined, ReadOutlined } from '@ant-design/icons';
import BellIcon from '@/components/icons/BellIcon';
import notificationService, { NotificationData } from '@/lib/services/notificationService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useIsDesktop } from '@/hooks/app/useIsDesktop';
import 'dayjs/locale/vi'; 
import { useIsAuthen } from '@/hooks/auth/useIsAuthen';
import styles from './styles.module.scss';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useTETranslation } from '@/hooks/app/useTETranslation';

// Initialize dayjs plugins
dayjs.extend(relativeTime);

// NotificationModal ref type
export interface NotificationModalRef {
  open: () => void;
  close: () => void;
}

const NotificationModal = forwardRef<NotificationModalRef>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [activeTab, setActiveTab] = useState<string>('my');
  const { t } = useTranslation()
  const isLoggedIn = useIsAuthen();
  const popoverRef = useRef<HTMLDivElement>(null);
  const { getValidDayjs } = useTETranslation()

  useEffect(() => {
    // Initialize notifications from service
    setNotifications(notificationService.getNotifications());

    // Add listener for notification changes
    const handleNotificationsChange = (updatedNotifications: NotificationData[]) => {
      setNotifications(updatedNotifications);
    };
    
    notificationService.addListener(handleNotificationsChange);
    
    // Clean up
    return () => {
      notificationService.removeListener(handleNotificationsChange);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  
  const handleMarkAllAsRead = () => {
    notificationService.markAllAsRead();
  };
  
  const handleMarkAsRead = (id: string) => {
    notificationService.markAsRead(id);
  };
  
  // Filter notifications by type
  const getFilteredNotifications = () => {
    if (!isLoggedIn || activeTab === 'all') {
      return notifications;
    } else if (activeTab === 'my') {
      return notifications.filter(notification => notification.notifiactionType === 'Personal');
    } else if (activeTab === 'general') {
      return notifications.filter(notification => notification.notifiactionType === 'All');
    }
    return notifications;
  };
  
  const unreadCount = notificationService.getUnreadCount();
  const filteredNotifications = getFilteredNotifications();

  const renderNotificationTime = (timestamp: number) => (
    <div className={styles.timestamp}>
      {getValidDayjs(timestamp).format('DD/MM/YYYY HH:mm:ss')}
    </div>
  );
  
  const renderNotificationIcon = (notification: NotificationData) => {
    if (notification.image) {
      return (
        <div className={styles.notificationImage}>
          <FontAwesomeIcon icon={faBell} />
        </div>
      );
    } else {
      // Default icon based on notification type
      return (
        <div className={styles.notificationImage}>
          <img 
            src="/static/deal_1.png"
            alt={`${process.env.NEXT_PUBLIC_PLATFORM_NAME || ""}`}
            width={40}
            height={40}
          />
        </div>
      );
    }
  };

  const notificationHeader = (
    <div className={styles.header}>
      {isLoggedIn ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className={styles.tabs}
          items={[
            { key: 'my', label: t("home.noti_personal") },
            { key: 'general', label: t("home.noti_all") },
          ]}
        />
      ) : (
        <div className={styles.allNotificationsHeader}>
          <div className={styles.title}>{t("home.noti_all")}</div>
          <Button type="link" onClick={handleMarkAllAsRead} className={styles.markAllButton}>
            {t("home.mark_all_as_read")}
          </Button>
        </div>
      )}
    </div>
  );

  const notificationListContent = (
    <List
      className={styles.notificationList}
      dataSource={filteredNotifications}
      locale={{ emptyText: <Empty description={t("home.noti_empty")} className={styles.emptyState} /> }}
      renderItem={(notification) => (
        <List.Item 
          key={notification.id}
          className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''}`}
          onClick={() => {
            if (!notification.read) {
              handleMarkAsRead(notification.id!);
            }
            if (notification.url) {
              window.open(notification.url, '_blank');
            }
          }}
        >
          <div className={styles.notificationContent}>
            <div className={styles.notificationIcon}>
              {renderNotificationIcon(notification)}
            </div>
            <div className={styles.notificationTextContent}>
              <div className={styles.notificationTitle}>{notification.title}</div>
              <div className={styles.notificationBody}>{notification.body}</div>
              {notification.timestamp && renderNotificationTime(notification.timestamp)}
            </div>
            <div className={styles.notificationActions}>
              {!notification.read && (
                <Button 
                  type="text" 
                  icon={<ReadOutlined />}
                  className={styles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead(notification.id!);
                  }}
                />
              )}
            </div>
          </div>
        </List.Item>
      )}
    />
  );

  const notificationContent = (
    <div className={styles.popoverContainer}>
      {notificationHeader}
      {notificationListContent}
    </div>
  );

  return (
    <div ref={popoverRef}>
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        content={notificationContent}
        trigger="click"
        placement="bottomRight"
        classNames={{
          root: styles.notificationPopover
        }}
        getPopupContainer={() => popoverRef.current || document.body}
        arrow={false}
      />
    </div>
  );
});

NotificationModal.displayName = 'NotificationModal';

export default NotificationModal;